import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { openWithCacheBuster } from "./cacheBuster";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, CornerUpRight, Link2 } from "lucide-react";
import type {
	PageIssue,
	LinksToRedirectDetails,
	RedirectingLinkTarget,
} from "@/types/technicalAudit";
import { sanitizeBrokenUrl } from "./issueBeforeAfter";

interface LinksToRedirectCardProps {
	issue: PageIssue;
}

function getStatusColor(code: number | null): string {
	if (code === null) return "bg-muted text-muted-foreground border-border";
	// 301/308 permanent (worth repointing) vs 302/307 temporary — both amber, but
	// permanent gets the deeper tone.
	if (code === 301 || code === 308)
		return "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-700";
	return "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800";
}

// Read-only evidence for a links_to_redirect issue: the source page plus every
// internal link on it that passes through a 3xx redirect. Source-page-anchored
// (mirrors LinksToBrokenCard) — the page is NOT its own target, so it never
// renders the page as its own "broken URL".
export function LinksToRedirectCard({ issue }: LinksToRedirectCardProps) {
	const details = (issue.issue_details ?? {}) as LinksToRedirectDetails;
	const redirectingLinks: RedirectingLinkTarget[] = Array.isArray(
		details.redirecting_links,
	)
		? details.redirecting_links
		: [];
	const redirectCount =
		typeof details.redirect_count === "number"
			? details.redirect_count
			: redirectingLinks.length;
	const sourcePage = details.page_url || issue.page_url;

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-900/40">
					<CornerUpRight className="w-5 h-5 text-amber-600 dark:text-amber-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-amber-900 dark:text-amber-300">Links to Redirect</h4>
					<p className="text-sm mt-1 text-amber-700 dark:text-amber-400">
						{redirectCount > 0 ? (
							<>
								This page has {redirectCount} internal{" "}
								{redirectCount === 1
									? "link that points"
									: "links that point"}{" "}
								at a URL that redirects (301/302). Repointing each link straight
								to its final destination is faster for visitors and cleaner for
								crawlers.
							</>
						) : (
							<>
								This page contains internal links that pass through a redirect.
								Generate a suggestion to resolve each link&rsquo;s final
								destination, then repoint them so links go direct.
							</>
						)}
					</p>
				</div>
				{redirectCount > 0 && (
					<Badge
						variant="outline"
						className="bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800"
					>
						{redirectCount} redirecting
					</Badge>
				)}
			</div>

			{redirectingLinks.length > 0 && (
				<Card className="border-border shadow-sm">
					<CardHeader className="pb-3">
						<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
							<div className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-950/40 flex items-center justify-center">
								<CornerUpRight className="w-4 h-4 text-amber-600 dark:text-amber-400" />
							</div>
							Redirecting links
						</CardTitle>
					</CardHeader>
					<CardContent className="pt-0">
						<ul className="space-y-2">
							{redirectingLinks.map((link, idx) => (
								<li
									key={`${link.target_url}:${idx}`}
									className="p-3 rounded-lg border bg-amber-50 dark:bg-amber-950/40/50 border-amber-200 dark:border-amber-800 space-y-1"
								>
									<div className="flex items-start justify-between gap-2">
										<p className="text-sm font-mono break-all text-amber-800 dark:text-amber-300 flex-1">
											{sanitizeBrokenUrl(link.target_url)}
										</p>
										<Badge
											variant="outline"
											className={getStatusColor(link.status_code)}
										>
											{link.status_code ?? "—"}
										</Badge>
									</div>
									{link.anchor && (
										<p className="text-xs text-muted-foreground">
											Anchor text: &ldquo;{link.anchor}&rdquo;
										</p>
									)}
								</li>
							))}
						</ul>
					</CardContent>
				</Card>
			)}

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<Link2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Source Page
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<a
						href={sourcePage}
						onClick={(e) => openWithCacheBuster(e, sourcePage)}
						target="_blank"
						rel="noopener noreferrer"
						className="group flex items-start gap-2 p-3 rounded-lg border bg-muted border-border hover:border-border hover:bg-muted transition-all"
					>
						<p className="text-sm font-mono break-all text-foreground flex-1">
							{sourcePage}
						</p>
						<ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
					</a>
				</CardContent>
			</Card>
		</div>
	);
}
